/* Adday desktop navigation: full-width dropdowns with a dimmed page.
   Triggers are buttons with aria-expanded. Clicking outside, choosing a link
   or pressing Escape closes the open dropdown. Only one is open at a time.
   Panels and the overlay fade and slide in with opacity and transform
   (adday-header.css); they are hidden only once the closing transition ends.
   Dropdowns open on click only (Enter or Space from the keyboard), never on hover
   or focus. */
(() => {
  const DESKTOP_QUERY = window.matchMedia('(min-width: 1000px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CLOSE_MS = 260; // a little longer than the 250ms transition

  function show(element) {
    if (!element) return;
    window.clearTimeout(element.addayHideTimer);
    element.hidden = false;
    void element.offsetWidth; // start the transition from the closed state
    element.classList.add('is-open');
  }

  function hide(element) {
    if (!element) return;
    element.classList.remove('is-open');
    window.clearTimeout(element.addayHideTimer);
    element.addayHideTimer = window.setTimeout(
      () => {
        if (!element.classList.contains('is-open')) element.hidden = true;
      },
      REDUCED_MOTION.matches ? 0 : CLOSE_MS
    );
  }

  class AddayNav {
    constructor(nav) {
      this.nav = nav;
      this.header = nav.closest('.site-header') || nav;
      this.backdrop = nav.querySelector('[data-adday-backdrop]');
      this.triggers = Array.from(nav.querySelectorAll('[data-adday-trigger]'));
      this.openTrigger = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.updateOffset = this.updateOffset.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);

      this.triggers.forEach((trigger) => {
        const item = trigger.parentElement;

        trigger.addEventListener('click', () => this.toggle(trigger));

        item.addEventListener('focusout', (event) => {
          if (this.openTrigger === trigger && event.relatedTarget && !item.contains(event.relatedTarget)) {
            this.close();
          }
        });

        const panel = this.getPanel(trigger);
        if (panel) {
          panel.addEventListener('click', (event) => {
            if (event.target.closest('a')) this.close();
          });
        }
      });

      DESKTOP_QUERY.addEventListener('change', this.onBreakpointChange);
    }

    getPanel(trigger) {
      return document.getElementById(trigger.getAttribute('aria-controls'));
    }

    updateOffset() {
      const bottom = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--adday-header-bottom', `${bottom}px`);
    }

    toggle(trigger) {
      if (this.openTrigger === trigger) {
        this.close();
      } else {
        this.open(trigger);
      }
    }

    open(trigger) {
      const panel = this.getPanel(trigger);
      if (!panel) return;

      if (this.openTrigger) this.close();

      this.updateOffset();
      trigger.setAttribute('aria-expanded', 'true');
      show(panel);
      show(this.backdrop);
      this.openTrigger = trigger;

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.updateOffset, { passive: true });
      window.addEventListener('resize', this.updateOffset);
    }

    close({ returnFocus = false } = {}) {
      const trigger = this.openTrigger;
      if (!trigger) return;

      const panel = this.getPanel(trigger);
      trigger.setAttribute('aria-expanded', 'false');
      hide(panel);
      hide(this.backdrop);
      this.openTrigger = null;

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.updateOffset);
      window.removeEventListener('resize', this.updateOffset);

      // Back on the trigger after Escape.
      if (returnFocus) trigger.focus();
    }

    onDocumentClick(event) {
      if (!this.openTrigger) return;
      if (this.openTrigger.parentElement.contains(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.openTrigger) {
        event.preventDefault();
        this.close({ returnFocus: true });
      }
    }

    onBreakpointChange(event) {
      if (!event.matches) this.close();
    }

    openByBlockId(blockId) {
      const panel = this.nav.querySelector(`[data-adday-panel][data-block-id="${blockId}"]`);
      if (!panel) return;
      const trigger = this.triggers.find((item) => item.getAttribute('aria-controls') === panel.id);
      if (trigger && this.openTrigger !== trigger) this.open(trigger);
    }

    destroy() {
      this.close();
      DESKTOP_QUERY.removeEventListener('change', this.onBreakpointChange);
    }
  }

  let instance = null;

  function init() {
    if (instance) instance.destroy();
    const nav = document.querySelector('[data-adday-nav]');
    instance = nav ? new AddayNav(nav) : null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-initialise when the header reloads, and show a dropdown
  // while its block is selected.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:block:select', (event) => {
      if (instance) instance.openByBlockId(event.detail.blockId);
    });
    document.addEventListener('shopify:block:deselect', () => {
      if (instance) instance.close();
    });
  }
})();

/* Adday Shop mega panel row (snippets/adday-nav-shop-mega.liquid).
   When the columns do not all fit, the row scrolls sideways and a progress
   bar with previous/next buttons shows under it. The thumb follows the scroll
   position; each button scrolls by one column and disables at the ends.
   Trackpads and shift + mouse wheel scroll the row natively. When everything
   fits, the bar and buttons stay hidden. */
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setUp(root) {
    if (root.dataset.addayShopmegaReady) return;
    root.dataset.addayShopmegaReady = 'true';

    const track = root.querySelector('[data-adday-shopmega-track]');
    const progress = root.querySelector('[data-adday-shopmega-progress]');
    const thumb = root.querySelector('[data-adday-shopmega-thumb]');
    const prev = root.querySelector('[data-adday-shopmega-prev]');
    const next = root.querySelector('[data-adday-shopmega-next]');
    if (!track || !progress || !thumb || !prev || !next) return;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const scrolls = track.clientWidth > 0 && max > 1;
      progress.hidden = !scrolls;
      if (!scrolls) return;

      const visible = track.clientWidth / track.scrollWidth;
      const position = Math.min(1, Math.max(0, track.scrollLeft / max));
      thumb.style.setProperty('--adday-shopmega-thumb', `${visible * 100}%`);
      // translateX percentages are of the thumb's own width.
      thumb.style.setProperty('--adday-shopmega-offset', `${(position * (1 - visible) * 100) / visible}%`);
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    };

    const stepWidth = () => {
      const column = track.querySelector('.adday-shopmega__col');
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return column ? column.getBoundingClientRect().width + gap : track.clientWidth;
    };

    const scrollByColumn = (direction) => {
      track.scrollBy({
        left: direction * stepWidth(),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
    };

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      },
      { passive: true }
    );
    prev.addEventListener('click', () => scrollByColumn(-1));
    next.addEventListener('click', () => scrollByColumn(1));

    // The panel is hidden until opened, so measure again whenever its size changes.
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(track);
    } else {
      window.addEventListener('resize', update);
    }
    update();
  }

  function init() {
    document.querySelectorAll('[data-adday-shopmega]').forEach(setUp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
